import * as React from 'react';
import styled from 'styled-components';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import H3 from '@sdk/components/ui/typography/H3';
import Text16 from '@sdk/components/ui/typography/Text16';
import Text14 from '@sdk/components/ui/typography/Text14';
import Href from '@sdk/components/ui/Href';
import Button from '@sdk/components/ui/buttons/Button';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import useConfig from '@sdk/config/useConfig';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { Feature } from '@sdk/common/features';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';

import { FeatureNames } from '@web/account/types';

import usePremiumGuard from '../../../premiumGuard/usePremiumGuard';
import { LaborCostFeaturePreview } from '../../../premiumGuard/premiumPreview/LaborCostFeaturePreview';
import { CustomerReviews } from '../../../premiumGuard/premiumPreview/CustomerReviews';
import { OtherPremiumFeatures } from '../../../premiumGuard/premiumPreview/OtherPremiumFeatures';

const YOUTUBE_VIDEO_LABOR_COST_URL = 'https://www.youtube.com/embed/aESEXCXT4v8';

const PremiumFeaturePreview = styled(Column)`
  flex: 1;
  background-color: ${({ theme }): string => theme.colors.white};
  box-shadow: 0 2px 8px 0 ${({ theme }): string => theme.colors.boxShadow};
`;

const PreviewBanner = styled(Row)`
  flex: 1;
  background:
    linear-gradient(47.18deg, rgba(0, 0, 0, 0.012) 63.26%, rgba(0, 0, 0, 0.052) 95.14%),
    linear-gradient(270deg, #f2f5f7 38.25%, #fafbfc 65.66%);
  padding: 40px 40px 40px 75px;
  border-radius: 4px;
`;

const PreviewBannerContent = styled(Column)`
  width: 360px;
`;

const PreviewBannerVideoWrapper = styled(Row)`
  position: relative;
  transition: transform 0.3s ease;
  margin-left: auto;

  &:hover {
    transform: scale(1.01);
  }
`;

const VideoOpener = styled(Row)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 2;
  cursor: pointer;
`;

type PremiumPreviewContentProps = {
  themeColor: ThemeColorType;
};
const PremiumPreviewContent = styled(Row)<PremiumPreviewContentProps>`
  background-color: ${({ theme, themeColor }): string => theme.colors[themeColor]};
`;

const GutterColumn = styled(Column)`
  flex: 1;
`;

const PremiumPreviewContentColumn = styled(Column)`
  flex: 3;
`;

export const LaborCostPremiumScreen: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { currentUser } = useAuth();
  const { APP_URL } = useConfig();
  const { getRequiredPlan, openPricingModal, openLaborCostVideoModal } = usePremiumGuard();

  const purchasePremium = (): void => {
    window.location.href = `${APP_URL}/account/purchase`;
  };

  const goToBilling = (): void => {
    window.location.href = `${APP_URL}/account/billing`;
  };

  return (
    <PremiumFeaturePreview>
      <PreviewBanner>
        <PreviewBannerContent>
          <Row pTop={20} pBottom={10}>
            <H3 themeColor={ThemeColorType.primary}>
              {gettext('Optimize your spending as you schedule')}
            </H3>
          </Row>
          <Row pBottom={10}>
            <Text16 bold themeColor={ThemeColorType.gray}>
              {gettext('Available in %(subscriptionName)s', {
                subscriptionName: getRequiredPlan(Feature.laborCost),
              })}
            </Text16>
          </Row>
          <Row pBottom={10}>
            <Text14 themeColor={ThemeColorType.gray}>
              {gettext(
                'Estimate and optimize your spending as you schedule. Upload projected sales to get your labor percentage, and avoid exceeding your labor budget or scheduling overtime.'
              )}
            </Text14>
          </Row>
          {currentUser.org.isToastBilling ? (
            <Href
              fontSize={14}
              semiBold
              themeColor={ThemeColorType.primary}
              onClick={() => goToBilling()}>
              {gettext('Learn more about Scheduling Pro >')}
            </Href>
          ) : (
            <Button onClick={purchasePremium} buttonType={ButtonType.primary}>
              {gettext('Purchase premium')}
            </Button>
          )}
        </PreviewBannerContent>
        <PreviewBannerVideoWrapper mRight={20}>
          <iframe
            width="428"
            height="240"
            src={YOUTUBE_VIDEO_LABOR_COST_URL}
            frameBorder="0"
            allowFullScreen></iframe>
          <VideoOpener onClick={openLaborCostVideoModal} />
        </PreviewBannerVideoWrapper>
      </PreviewBanner>

      <PremiumPreviewContent themeColor={ThemeColorType.white}>
        <GutterColumn />
        <PremiumPreviewContentColumn>
          <LaborCostFeaturePreview />
        </PremiumPreviewContentColumn>
        <GutterColumn />
      </PremiumPreviewContent>

      <PremiumPreviewContent themeColor={ThemeColorType.bodyBg}>
        <GutterColumn />
        <PremiumPreviewContentColumn>
          <CustomerReviews purchasePremium={purchasePremium} />
        </PremiumPreviewContentColumn>
        <GutterColumn />
      </PremiumPreviewContent>

      <OtherPremiumFeatures
        user={currentUser}
        hiddenKeys={[FeatureNames.laborCost]}
        openPricingModal={openPricingModal}
      />
    </PremiumFeaturePreview>
  );
};
